import {
  Component,
  computed,
  Input,
  input,
  signal,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MenuItem } from 'primeng/api';
import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-menu-item',
  standalone: true,
  imports: [CommonModule, RouterModule, TranslateModule],
  templateUrl: 'menu-item.component.html',
  styleUrl: './menu-item.component.scss',
})
export class MenuItemComponent implements OnInit {
  @Input() collapsed = false;

  item = input.required<MenuItem>();

  isExpanded = signal(false);
  currentUrl = signal('');

  hasSubmenu = computed(
    () => !!(this.item().items && this.item().items!.length > 0)
  );

  constructor(private router: Router) {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.currentUrl.set(this.router.url);
        if (this.isActiveRoute() || this.hasActiveChild()) {
          this.isExpanded.set(true);
        }
      });
  }

  ngOnInit(): void {
    this.currentUrl.set(this.router.url);
    if (this.isActiveRoute() || this.hasActiveChild()) {
      this.isExpanded.set(true);
    }
  }

  toggleSubmenu(event: MouseEvent): void {
    if (this.hasSubmenu()) {
      event.preventDefault();
      this.isExpanded.update((value) => !value);
    }
  }

  isActiveRoute(): boolean {
    if (!this.item().routerLink) return false;

    const routerLink = this.normalizeRouterLink(this.item().routerLink);
    return this.currentUrl() === routerLink;
  }

  hasActiveChild(): boolean {
    if (!this.item().items) return false;

    return this.item().items!.some((child) => {
      const childRouterLink = this.normalizeRouterLink(child.routerLink as any);

      const isDirectMatch =
        childRouterLink && this.currentUrl() === childRouterLink;

      const isNestedMatch =
        childRouterLink &&
        childRouterLink !== '/' &&
        this.currentUrl().startsWith(childRouterLink);

      const hasActiveGrandchild =
        child.items &&
        child.items.length > 0 &&
        this.checkChildrenActive(child.items);

      return isDirectMatch || isNestedMatch || hasActiveGrandchild;
    });
  }

  private checkChildrenActive(items: MenuItem[]): boolean {
    return items.some((item) => {
      if (!item.routerLink) return false;

      let itemRouterLink: string;
      if (Array.isArray(item.routerLink)) {
        itemRouterLink = '/' + item.routerLink.join('/');
      } else {
        itemRouterLink = item.routerLink as string;
      }

      const isMatch =
        this.currentUrl() === itemRouterLink ||
        (itemRouterLink !== '/' &&
          this.currentUrl().startsWith(itemRouterLink));

      const hasActiveChild =
        item.items &&
        item.items.length > 0 &&
        this.checkChildrenActive(item.items);

      return isMatch || hasActiveChild;
    });
  }

  routerLinkExact(): boolean {
    if (this.item().routerLink && Array.isArray(this.item().routerLink)) {
      return (
        this.item().routerLink.length === 1 && this.item().routerLink[0] === '/'
      );
    }
    return false;
  }

  private normalizeRouterLink(link: string | string[] | undefined): string {
    if (!link) return '';

    if (Array.isArray(link)) {
      const segments = link.map((s) => s.replace(/^\/+/, ''));
      return '/' + segments.join('/');
    }

    let s = link;
    if (!s.startsWith('/')) {
      s = '/' + s;
    }
    return s;
  }
}
